import { useEffect, useRef, useState } from 'react';
import clienteAxios from '../../config/axios';
import '../Auth/auth.css';
import '../mercado/mis.css';
import { useT } from 'i18n';

const MAX_MB = 8;
const TIPOS = ['image/jpeg', 'image/png', 'application/pdf'];

const SLOTS = [
  { key: 'file_id_front', titulo: 'kyc.docTitulo', ayuda: 'kyc.docAyuda' },
  { key: 'file_id_reverse', titulo: 'kyc.resTitulo', ayuda: 'kyc.resAyuda' },
  { key: 'file_selfie', titulo: 'kyc.selfieTitulo', ayuda: 'kyc.selfieAyuda' },
];

const Slot = ({ slot, archivo, vista, onElegir, onQuitar, error }) => {
  const { t } = useT();
  const input = useRef(null);
  return (
    <div className={`p7k-slot${archivo ? ' lleno' : ''}`}>
      <div className="p7k-slot-info">
        <strong>{t(slot.titulo)}</strong>
        <span>{t(slot.ayuda)}</span>
      </div>
      {archivo ? (
        <div className="p7k-listo">
          {vista ? <img src={vista} alt={t('kyc.vistaPrevia', { titulo: t(slot.titulo) })} /> : <div className="p7k-pdf">PDF</div>}
          <div>
            <span title={archivo.name}>{archivo.name}</span>
            <small>{(archivo.size / 1024 / 1024).toFixed(1)} MB</small>
            <button type="button" onClick={() => { onQuitar(); if (input.current) input.current.value = ''; }}>{t('kyc.cambiar')}</button>
          </div>
        </div>
      ) : (
        <label className="p7k-elegir" htmlFor={slot.key}>{t('kyc.elegirArchivo')}<small>{t('kyc.formatosMax', { max: MAX_MB })}</small></label>
      )}
      <input ref={input} id={slot.key} className="p7k-oculto" type="file" accept="image/jpeg,image/png,application/pdf" onChange={onElegir} />
      {error && <span className="p7k-error" role="alert">{error}</span>}
    </div>
  );
};

const Kyc = ({ setValue, userStatus, guardarAppBar, guardarAppBarKyc, appbar, appbarkyc }) => {
  const { t, ts } = useT();
  const [archivos, setArchivos] = useState({});
  const [vistas, setVistas] = useState({});
  const [errores, setErrores] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null); // { ok, texto }

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => () => Object.values(vistas).forEach((v) => v && URL.revokeObjectURL(v)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const elegir = (key) => (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    let err = '';
    if (!TIPOS.includes(f.type)) err = t('kyc.formatoNoPermitido');
    else if (f.size > MAX_MB * 1024 * 1024) err = t('kyc.superaMax', { max: MAX_MB });
    if (err) {
      e.target.value = '';
      setErrores((x) => ({ ...x, [key]: err }));
      return;
    }
    setErrores((x) => ({ ...x, [key]: '' }));
    setArchivos((x) => ({ ...x, [key]: f }));
    setVistas((x) => {
      if (x[key]) URL.revokeObjectURL(x[key]);
      return { ...x, [key]: f.type.startsWith('image/') ? URL.createObjectURL(f) : null };
    });
  };

  const quitar = (key) => () => {
    setArchivos((x) => ({ ...x, [key]: null }));
    setVistas((x) => { if (x[key]) URL.revokeObjectURL(x[key]); return { ...x, [key]: null }; });
  };

  const completo = SLOTS.every((s) => archivos[s.key]);

  const enviar = async (e) => {
    e.preventDefault();
    if (!completo) return;
    setEnviando(true);
    setResultado(null);
    const fd = new FormData();
    SLOTS.forEach((s) => fd.append(s.key, archivos[s.key], archivos[s.key].name));
    try {
      const r = await clienteAxios.post('/api/client/kyc', fd, { headers: { 'Content-Type': 'multipart/form-data', Accept: 'application/json' } });
      setResultado({ ok: true, texto: ts(r.data.message) || t('kyc.enviados') });
    } catch (err) {
      setResultado({ ok: false, texto: ts(err.response?.data?.message) || t('kyc.errorSubir') });
    } finally {
      setEnviando(false);
    }
  };

  const continuar = () => {
    if (typeof setValue === 'function') setValue(2);
    if (typeof guardarAppBar === 'function') guardarAppBar({ ...appbar, primero: 0, segundo: 0, tercero: 1 });
    if (typeof guardarAppBarKyc === 'function') guardarAppBarKyc({ ...appbarkyc, primero: 0, segundo: 0, tercero: 1 });
    if (typeof userStatus === 'function') userStatus();
    setResultado(null);
  };

  if (resultado && resultado.ok) {
    return (
      <div className="p7k">
        <div className="p7a-estado">
          <div className="p7a-icono ok" aria-hidden="true">✓</div>
          <h2>{t('kyc.recibidosTitulo')}</h2>
          <p>{t('kyc.recibidosDetalle', { texto: resultado.texto })}</p>
          <button type="button" className="p7-btn-gold" onClick={continuar}>{t('kyc.continuar')}</button>
        </div>
      </div>
    );
  }

  return (
    <form className="p7k" onSubmit={enviar}>
      <header className="p7k-head">
        <h3>{t('kyc.verificaTitulo')}</h3>
        <p>{t('kyc.verificaAyuda')}</p>
      </header>

      {resultado && !resultado.ok && <div className="p7a-aviso err" role="alert"><div>{resultado.texto}</div></div>}

      <div className="p7k-grid">
        {SLOTS.map((s) => (
          <Slot key={s.key} slot={s} archivo={archivos[s.key]} vista={vistas[s.key]} onElegir={elegir(s.key)} onQuitar={quitar(s.key)} error={errores[s.key]} />
        ))}
      </div>

      <div className="p7k-pie">
        <small>{t('kyc.soloVerificar')}</small>
        <button type="submit" className="p7-btn-gold" disabled={!completo || enviando}>{enviando ? t('kyc.subiendo') : t('kyc.enviarDocs')}</button>
      </div>
    </form>
  );
};

export default Kyc;
